import { type FC, Suspense, useEffect, useRef, useState } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { StyledMapContainer, StyledContainer } from '../../../pages/OneDesignDetailPages/styles'
import SidebarMap from './SidebarMap'
import useWindowWidth from '../../hooks/useWindowWidth'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import DetailContainer from '../../../pages/OneDesignDetailPages/DetailComponents/DetailContainer'
import { Breakpoint } from '../../hooks/useBreakpoint'
import MobileMapOverlay from '../../../pages/MapPage/components/MobileMapOverlay'
import { useMapContext } from '../../contexts/map/MapContext'
import MapBanner from '../../../pages/MapPage/components/MapBanner'
import { useIsEmbedded } from '../../contexts/ui'

const StyledLoadingSpinner = styled(LoadingSpinner)`
  margin-top: 200px;
`

const MobileOverlayWrapper = styled.div`
  position: relative;
  height: calc(100vh - var(--app-menu-height));
  width: 100%;
`

const MapWrapperStyle = createGlobalStyle<{ hideHeader: boolean }>`
  header, #BetaToggle {
    display: ${({ hideHeader }) => (hideHeader ? 'none !important' : 'flex !important')};
  }
`

const SidebarMapWrapper: FC<{ children: JSX.Element }> = ({ children }) => {
  const { setLegendActive, showMap, showLargeMap } = useMapContext()
  const isEmbedded = useIsEmbedded()
  const width = useWindowWidth()
  const [mobileMapOpen, setMobileMapOpen] = useState(false)
  const isMobile = width < Breakpoint.Md.valueOf()
  const showMobileOverlay = showMap && showLargeMap && isMobile && !mobileMapOpen
  const mapVisible = showMap && !showMobileOverlay
  const hideHeader = isMobile && showLargeMap && mobileMapOpen
  const [mapWidth, setMapWidth] = useState<number>(0)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref?.current) {
      setMapWidth(ref.current.offsetWidth)
    }
  }, [ref.current, showLargeMap])

  useEffect(() => {
    const handleResize = () => setMapWidth(ref?.current?.offsetWidth ?? 0)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <>
      <MapWrapperStyle hideHeader={hideHeader} />
      <StyledContainer $isEmbedded={isEmbedded}>
        <Suspense
          fallback={
            <DetailContainer>
              <StyledLoadingSpinner />
            </DetailContainer>
          }
        >
          {children}
        </Suspense>
        {mapVisible && (
          <StyledMapContainer
            $hideHeader={hideHeader}
            $showMobile={showLargeMap}
            $showLargeMap={showLargeMap}
            data-testid="mapContainer"
            ref={ref}
          >
            <SidebarMap isMobile={isMobile} onCloseMobileMap={() => setMobileMapOpen(false)} />
            {mapWidth > 830 && <MapBanner />}
          </StyledMapContainer>
        )}
        {showMobileOverlay && (
          <MobileOverlayWrapper>
            <MobileMapOverlay
              onOpenMobileMap={() => {
                setMobileMapOpen(true)
                setLegendActive(false)
              }}
            />
          </MobileOverlayWrapper>
        )}
      </StyledContainer>
    </>
  )
}

export default SidebarMapWrapper
